import { MentionMetadataTypesEnum } from '../enums/mention-metadata-types.enum';
import { MentionContentCustomizationEnum } from '../../../Common/Mentions/enums/mention-content-customization.enum';
import { mentionMetadataDefaultOrder } from './mention-metadata-default-order.const';
import { defaultSelectedMentionMetadataTypes } from './default-selected-mention-metadata-types.const';
import { MonitoringTextStyleModel } from './monitoring-text-style.model';
import { BriefingStyleStateEnum } from '../../../Common/MonitorShared/enum/briefing-style-state.enum';
import { GenericStyleModel } from '../../../Common/Styles/models/generic-style.model';
import { StyleStateEnum } from '../../../Common/PageBuilder/enums/style-state.enum';
import { UserCurrency } from '../../../Core/Users/enums/user-currency';
import { PaddingModel } from '../../../Common/PageBuilder/models/padding.model';
import { InsightsDisplayNameEnum } from '../../../Common/Mentions/enums/insights-display-name.enum';
import { isBooleanEqual } from '../../../Common/Misc/helpers/operator/operator';
import { ImageSizeEnum } from '../enums/image-size.enum';
import { MentionCardHeightEnum } from '../../../Common/Mentions/enums/mention-card-height.enum';

export class MentionCustomizationModel extends GenericStyleModel {
    mentionContent: MentionContentCustomizationEnum;
    metadataTypes: MentionMetadataTypesEnum[] = defaultSelectedMentionMetadataTypes.slice();
    mediaType: boolean = false;
    outletIcon: boolean = true;
    image: boolean = false;
    imageSize: ImageSizeEnum;
    notes: boolean = false;
    displayNotesLabel: boolean = true;
    includeBackToTopLinks: boolean = false;
    enforceConcisePlayer: boolean = false;
    highlightKeywords: boolean = false;
    notesLabel: string = 'Notes:';
    backToTopLinksLabel: string = 'Back to Top';
    sectionHeading: MonitoringTextStyleModel;
    heading: MonitoringTextStyleModel;
    snippetFont: MonitoringTextStyleModel;
    insightsFont: MonitoringTextStyleModel;
    metadataFont: MonitoringTextStyleModel;
    notesFont: MonitoringTextStyleModel;
    backToTopLinksFont: MonitoringTextStyleModel;
    mentionInsightsLinkFont: MonitoringTextStyleModel;
    timezone: string = null;
    dateFormat: string;
    locale: string;
    backgroundColor: string;
    preferredCurrency: UserCurrency;
    currencyRate: number;
    padding: PaddingModel;
    showInsights: boolean;
    displayInsightsName: boolean;
    insightsNameDisplay: InsightsDisplayNameEnum;
    customInsightsName: string;
    mentionInsightsLinkLabel: string;
    underlineLinks: boolean;
    showSectionHeading: boolean = true;
    accentColor: string;
    cardHeight: MentionCardHeightEnum;

    private metadataOrder: MentionMetadataTypesEnum[] = mentionMetadataDefaultOrder.slice();

    constructor(apiModel?: any) {
        super(apiModel);

        if (apiModel) {
            this.setFromApiModel(apiModel);
        } else {
            this.sectionHeading = new MonitoringTextStyleModel();
            this.heading = new MonitoringTextStyleModel();
            this.snippetFont = new MonitoringTextStyleModel();
            this.insightsFont = new MonitoringTextStyleModel();
            this.metadataFont = new MonitoringTextStyleModel();
            this.notesFont = new MonitoringTextStyleModel();
            this.backToTopLinksFont = new MonitoringTextStyleModel();
            this.mentionInsightsLinkFont = new MonitoringTextStyleModel();
            this.backgroundColor = '#ffffff';
            this.cardHeight = MentionCardHeightEnum.fixed;
        }

        if (!this.padding){
            this.padding = new PaddingModel();
        }

        if (!this.styleState || this.styleState === StyleStateEnum.DEFAULT) {
            this.styleState = BriefingStyleStateEnum.BRIEFING_1;
        }
        if (!this.displayStyleState || this.displayStyleState === StyleStateEnum.DEFAULT) {
            this.displayStyleState = this.styleState;
        }
        if (
            (
                !this.defaultReference ||
                this.defaultReference === StyleStateEnum.DEFAULT
            ) &&
            typeof this.styleState === 'string'
        ) {
            // Legacy data may not have default reference set, if the styleState is custom and there's no default reference, the default reference
            // should be BRIEFING_1 because it must have been created back when there was only one default
            this.defaultReference = this.styleState !== StyleStateEnum.CUSTOM ? this.styleState as string : BriefingStyleStateEnum.BRIEFING_1;
        }
    }

    clone(): MentionCustomizationModel {
        const clone = new MentionCustomizationModel(
            {
                mentionContent: this.mentionContent,
                metadataTypes: this.metadataTypes,
                metadataOrder: this.metadataOrder,
                mediaType: this.mediaType,
                outletIcon: this.outletIcon,
                image: this.image,
                imageSize: this.imageSize,
                notes: this.notes,
                displayNotesLabel: this.displayNotesLabel,
                includeBackToTopLinks: this.includeBackToTopLinks,
                enforceConcisePlayer: this.enforceConcisePlayer,
                highlightKeywords: this.highlightKeywords,
                notesLabel: this.notesLabel,
                backToTopLinksLabel: this.backToTopLinksLabel,
                sectionHeading: this.sectionHeading.toApi ? this.sectionHeading.toApi() : this.sectionHeading,
                heading: this.heading.toApi ? this.heading.toApi() : this.heading,
                snippetFont: this.snippetFont,
                insightsFont: this.insightsFont,
                mentionInsightsLinkFont: this.mentionInsightsLinkFont,
                metadataFont: this.metadataFont,
                notesFont: this.notesFont,
                timezone: this.timezone,
                dateFormat: this.dateFormat,
                locale: this.locale,
                backToTopLinksFont: this.backToTopLinksFont,
                styleState: this.styleState,
                displayStyleState: this.displayStyleState,
                defaultReference: this.defaultReference,
                backgroundColor: this.backgroundColor,
                preferredCurrency: this.preferredCurrency,
                currencyRate: this.currencyRate,
                padding: this.padding,
                showInsights: this.showInsights,
                displayInsightsName: this.displayInsightsName,
                insightsNameDisplay: this.insightsNameDisplay,
                customInsightsName: this.customInsightsName,
                mentionInsightsLinkLabel: this.mentionInsightsLinkLabel,
                underlineLinks: this.underlineLinks,
                showSectionHeading: this.showSectionHeading,
                accentColor: this.accentColor,
                cardHeight: this.cardHeight
            }
        );

        if (this.template) {
            clone.template = this.template.clone();
        }

        return clone;
    }

    toBlockStyle(): any {
        if (!this.isSavedStyle()) {
            return {
                mentionContent: this.mentionContent,
                metadataTypes: this.metadataTypes,
                metadataOrder: this.metadataOrder,
                mediaType: this.mediaType,
                outletIcon: this.outletIcon,
                image: this.image,
                imageSize: this.imageSize,
                notes: this.notes,
                displayNotesLabel: this.displayNotesLabel,
                notesLabel: this.notesLabel,
                backToTopLinksLabel: this.backToTopLinksLabel,
                sectionHeading: this.sectionHeading,
                heading: this.heading,
                snippetFont: this.snippetFont,
                insightsFont: this.insightsFont,
                mentionInsightsLinkFont: this.mentionInsightsLinkFont,
                metadataFont: this.metadataFont,
                notesFont: this.notesFont,
                includeBackToTopLinks: this.includeBackToTopLinks,
                enforceConcisePlayer: this.enforceConcisePlayer,
                highlightKeywords: this.highlightKeywords,
                backToTopLinksFont: this.backToTopLinksFont,
                timezone: this.timezone,
                dateFormat: this.dateFormat,
                locale: this.locale,
                styleState: this.styleState,
                displayStyleState: this.displayStyleState,
                backgroundColor: this.backgroundColor,
                preferredCurrency: this.preferredCurrency,
                currencyRate: this.currencyRate,
                padding: this.padding,
                showInsights: this.showInsights,
                displayInsightsName: this.displayInsightsName,
                insightsNameDisplay: this.insightsNameDisplay,
                customInsightsName: this.customInsightsName,
                mentionInsightsLinkLabel: this.mentionInsightsLinkLabel,
                underlineLinks: this.underlineLinks,
                showSectionHeading: this.showSectionHeading,
                accentColor: this.accentColor,
                cardHeight: this.cardHeight
            };
        } else {
            return {
                styleState: this.styleState
            };
        }
    }

    toApi(): any {
        return {
            mentionContent: this.mentionContent,
            metadataTypes: this.metadataTypes,
            metadataOrder: this.metadataOrder,
            mediaType: this.mediaType,
            outletIcon: this.outletIcon,
            image: this.image,
            imageSize: this.imageSize,
            includeBackToTopLinks: this.includeBackToTopLinks,
            enforceConcisePlayer: this.enforceConcisePlayer,
            highlightKeywords: this.highlightKeywords,
            notes: this.notes,
            displayNotesLabel: this.displayNotesLabel,
            notesLabel: this.notesLabel,
            backToTopLinksLabel: this.backToTopLinksLabel,
            sectionHeading: this.sectionHeading,
            heading: this.heading,
            snippetFont: this.snippetFont,
            insightsFont: this.insightsFont,
            mentionInsightsLinkFont: this.mentionInsightsLinkFont,
            metadataFont: this.metadataFont,
            notesFont: this.notesFont,
            backToTopLinksFont: this.backToTopLinksFont,
            timezone: this.timezone,
            dateFormat: this.dateFormat,
            locale: this.locale,
            defaultReference: this.defaultReference ? this.defaultReference : 'BRIEFING_1',
            backgroundColor: this.backgroundColor,
            preferredCurrency: this.preferredCurrency,
            currencyRate: this.currencyRate,
            padding: this.padding,
            showInsights: this.showInsights,
            displayInsightsName: this.displayInsightsName,
            insightsNameDisplay: this.insightsNameDisplay,
            customInsightsName: this.customInsightsName,
            mentionInsightsLinkLabel: this.mentionInsightsLinkLabel,
            underlineLinks: this.underlineLinks,
            showSectionHeading: this.showSectionHeading,
            accentColor: this.accentColor,
            cardHeight: this.cardHeight
        };
    }

    setFromApiModel(apiModel: any): void {
        super.setFromApiModel(apiModel);

        if (typeof apiModel === 'string') {
            apiModel = JSON.parse(apiModel);
        }

        if (apiModel.hasOwnProperty('mentionContent')) {
            this.mentionContent = apiModel.mentionContent;
        }
        if (apiModel.hasOwnProperty('showInsights')) {
            this.showInsights = apiModel.showInsights;
        }
        if (apiModel.hasOwnProperty('displayInsightsName')) {
            this.displayInsightsName = apiModel.displayInsightsName;
        }
        if (apiModel.hasOwnProperty('insightsNameDisplay')) {
            this.insightsNameDisplay = apiModel.insightsNameDisplay;
        }
        if (apiModel.hasOwnProperty('customInsightsName')) {
            this.customInsightsName = apiModel.customInsightsName;
        }
        if (apiModel.hasOwnProperty('mentionInsightsLinkLabel')) {
            this.mentionInsightsLinkLabel = apiModel.mentionInsightsLinkLabel;
        }
        if (apiModel.hasOwnProperty('metadataTypes')) {
            this.metadataTypes = apiModel.metadataTypes;
        }
        if (apiModel.hasOwnProperty('mediaType')) {
            this.mediaType = apiModel.mediaType;
        }
        if (apiModel.hasOwnProperty('outletIcon')) {
            this.outletIcon = apiModel.outletIcon;
        } else {
            this.outletIcon = false;
        }
        if (apiModel.hasOwnProperty('image')) {
            this.image = apiModel.image;
        }
        if (apiModel.hasOwnProperty('imageSize')) {
            this.imageSize = apiModel.imageSize;
        }
        if (apiModel.hasOwnProperty('includeBackToTopLinks')) {
            this.includeBackToTopLinks = apiModel.includeBackToTopLinks;
        }
        if (apiModel.hasOwnProperty('enforceConcisePlayer')) {
            this.enforceConcisePlayer = apiModel.enforceConcisePlayer;
        }
        if (apiModel.hasOwnProperty('highlightKeywords')) {
            this.highlightKeywords = apiModel.highlightKeywords;
        }
        if (apiModel.hasOwnProperty('notes')) {
            this.notes = apiModel.notes;
        }
        if (apiModel.hasOwnProperty('displayNotesLabel')) {
            this.displayNotesLabel = apiModel.displayNotesLabel;
        }
        if (apiModel.hasOwnProperty('notesLabel')) {
            this.notesLabel = apiModel.notesLabel;
        }
        if (apiModel.hasOwnProperty('backToTopLinksLabel')) {
            this.backToTopLinksLabel = apiModel.backToTopLinksLabel;
        }
        if (apiModel.hasOwnProperty('sectionHeading')) {
            this.sectionHeading = new MonitoringTextStyleModel(apiModel.sectionHeading);
        } else {
            this.sectionHeading = new MonitoringTextStyleModel();
        }
        if (apiModel.hasOwnProperty('heading')) {
            this.heading = new MonitoringTextStyleModel(apiModel.heading);
        } else {
            this.heading = new MonitoringTextStyleModel();
        }
        if (apiModel.hasOwnProperty('underlineLinks')) {
            this.underlineLinks = apiModel.underlineLinks;
        } else {
            this.underlineLinks = false;
        }
        if (apiModel.hasOwnProperty('showSectionHeading')) {
            this.showSectionHeading = apiModel.showSectionHeading;
        } else {
            this.showSectionHeading = true;
        }
        if (apiModel.hasOwnProperty('snippetFont')) {
            this.snippetFont = new MonitoringTextStyleModel(apiModel.snippetFont);
        } else {
            this.snippetFont = new MonitoringTextStyleModel();
        }
        if (apiModel.hasOwnProperty('insightsFont')) {
            this.insightsFont = new MonitoringTextStyleModel(apiModel.insightsFont);
        } else {
            this.insightsFont = new MonitoringTextStyleModel();
        }
        if (apiModel.hasOwnProperty('mentionInsightsLinkFont')) {
            this.mentionInsightsLinkFont = new MonitoringTextStyleModel(apiModel.mentionInsightsLinkFont);
        } else {
            this.mentionInsightsLinkFont = new MonitoringTextStyleModel();
        }
        if (apiModel.hasOwnProperty('metadataFont')) {
            this.metadataFont = new MonitoringTextStyleModel(apiModel.metadataFont);
        } else {
            this.metadataFont = new MonitoringTextStyleModel();
        }

        this.metadataFont.alignment = null;

        if (apiModel.hasOwnProperty('notesFont')) {
            this.notesFont = new MonitoringTextStyleModel(apiModel.notesFont);
        } else {
            this.notesFont = new MonitoringTextStyleModel();
        }
        if (apiModel.hasOwnProperty('backToTopLinksFont')) {
            this.backToTopLinksFont = new MonitoringTextStyleModel(apiModel.backToTopLinksFont);
        } else {
            this.backToTopLinksFont = new MonitoringTextStyleModel();
        }

        if (apiModel.hasOwnProperty('timezone')) {
            this.timezone = apiModel.timezone;
        }

        if (apiModel.hasOwnProperty('dateFormat')) {
            this.dateFormat = apiModel.dateFormat;
        }

        if (apiModel.hasOwnProperty('locale')) {
            this.locale = apiModel.locale;
        }

        if (apiModel.hasOwnProperty('backgroundColor')) {
            this.backgroundColor = apiModel.backgroundColor;
        }

        if (apiModel.hasOwnProperty('preferredCurrency')) {
            this.preferredCurrency = apiModel.preferredCurrency;
        }

        if (apiModel.hasOwnProperty('currencyRate')) {
            this.currencyRate = apiModel.currencyRate;
        }

        if (apiModel.hasOwnProperty('padding')) {
            this.padding = new PaddingModel(apiModel.padding.paddingAll, apiModel.padding.paddingOption, apiModel.padding.topPadding,
                apiModel.padding.leftPadding, apiModel.padding.bottomPadding, apiModel.padding.rightPadding);
        }

        if (apiModel.hasOwnProperty('accentColor')) {
            this.accentColor = apiModel.accentColor;
        }
        if (apiModel.hasOwnProperty('cardHeight')) {
            this.cardHeight = apiModel.cardHeight;
        }
    }


    /**
     * Compares two MentionCustomizationModels for equality
     */
    equals(
        other: MentionCustomizationModel,
        compareTemplate: boolean,
        compareGenericStyles: boolean = true,
        compareDateFormat: boolean = true,
        compareCurrencyRate: boolean = true
    ): boolean {
        if (other === null || other === undefined) {
            return false;
        }
        if (compareGenericStyles && !super.equals(other, compareTemplate)) {
            return false;
        }
        if (this.mentionContent !== other.mentionContent) {
            return false;
        }
        if (this.showInsights !== other.showInsights) {
            return false;
        }
        if (this.displayInsightsName !== other.displayInsightsName) {
            return false;
        }
        if (this.insightsNameDisplay !== other.insightsNameDisplay) {
            return false;
        }
        if (this.customInsightsName !== other.customInsightsName) {
            return false;
        }
        if (this.mentionInsightsLinkLabel !== other.mentionInsightsLinkLabel) {
            return false;
        }
        if (JSON.stringify(this.metadataTypes) !== JSON.stringify(other.metadataTypes)) {
            return false;
        }
        if (JSON.stringify(this.metadataOrder) !== JSON.stringify(other.metadataOrder)) {
            return false;
        }
        if (this.mediaType !== other.mediaType) {
            return false;
        }
        if (this.outletIcon !== other.outletIcon) {
            return false;
        }
        if (this.image !== other.image) {
            return false;
        } else if (this.image && other.image && this.imageSize !== other.imageSize) {
            return false;
        }
        if (this.imageSize !== other.imageSize) {
            return false;
        }
        if (this.includeBackToTopLinks !== other.includeBackToTopLinks) {
            return false;
        }
        if (this.notes !== other.notes) {
            return false;
        }
        if (this.displayNotesLabel !== other.displayNotesLabel) {
            return false;
        }
        if (this.notesLabel !== other.notesLabel) {
            return false;
        }
        if (this.backToTopLinksLabel !== other.backToTopLinksLabel) {
            return false;
        }
        if (!this.heading.equals(other.heading, false)) {
            return false;
        }
        if (!isBooleanEqual(this.underlineLinks, other.underlineLinks)) {
            return false;
        }
        if (!isBooleanEqual(this.showSectionHeading, other.showSectionHeading)) {
            return false;
        }
        if (!this.sectionHeading.equals(other.sectionHeading, false)) {
            return false;
        }
        if (!this.snippetFont.equals(other.snippetFont, false)) {
            return false;
        }
        if (!this.insightsFont.equals(other.insightsFont, false)) {
            return false;
        }
        if (!this.mentionInsightsLinkFont.equals(other.mentionInsightsLinkFont, false)) {
            return false;
        }
        if (!this.metadataFont.equals(other.metadataFont, false)) {
            return false;
        }
        if (!this.notesFont.equals(other.notesFont, false)) {
            return false;
        }
        if (!this.backToTopLinksFont.equals(other.backToTopLinksFont, false)) {
            return false;
        }
        if (this.timezone !== other.timezone) {
            return false;
        }
        if (compareDateFormat && this.dateFormat !== other.dateFormat) {
            return false;
        }
        if (this.backgroundColor !== other.backgroundColor) {
            return false;
        }
        if (this.enforceConcisePlayer !== other.enforceConcisePlayer) {
            return false;
        }

        if (this.highlightKeywords !== other.highlightKeywords) {
            return false;
        }

        if (this.preferredCurrency !== other.preferredCurrency) {
            return false;
        }

        if (compareCurrencyRate && this.currencyRate !== other.currencyRate) {
            return false;
        }

        if (!this.padding.equals(other.padding)){
            return false;
        }

        if (this.accentColor !== other.accentColor){
            return false;
        }
        if (this.cardHeight !== other.cardHeight) {
            return false;
        }

        if (this.locale !== other.locale) {
            return false;
        }

        return true;
    }

    /**
     * Setter for the metadata order
     */
    setMetadataOrder(metadataOrder: MentionMetadataTypesEnum[]): void {
        this.metadataOrder = metadataOrder;
        this.sortMetadataTypes();
    }

    /**
     * Setter for metadataTypes
     */
    setMetadataTypes(metadataTypes: MentionMetadataTypesEnum[]): void {
        this.metadataTypes = metadataTypes;
        this.setMediaType();
        this.setOutletIcon();
        this.setImage();
        this.sortMetadataTypes();
    }

    /**
     * Sorts metadataTypes based on defined sort order
     */
    private sortMetadataTypes(): void {
        const selectedMetadata = this.metadataTypes.slice();

        // The metadata order needs to have all the metadata types or it ends up dropping metadata.
        if (this.metadataOrder.length !== mentionMetadataDefaultOrder.length) {
            // Appends any missing metadata types to the model's metadata order.
            this.metadataOrder = this.metadataOrder.concat(
                mentionMetadataDefaultOrder.slice().filter(metadata => {
                    return this.metadataOrder.indexOf(metadata) < 0;
                })
            );
        }

        this.metadataTypes = this.metadataOrder.filter(metadata => {
            return selectedMetadata.indexOf(metadata) >= 0;
        });
    }

    /**
     * Sets flag for mediaType
     */
    private setMediaType(): void {
        this.mediaType = !!this.metadataTypes.find(metadata => {
            return metadata === MentionMetadataTypesEnum.mediaType;
        });
    }

    /**
     * Sets flag for Outlet Icon
     */
    private setOutletIcon(): void {
        this.outletIcon = this.metadataTypes.includes(MentionMetadataTypesEnum.outletIcon);
    }

    /**
     * Sets flag for Icon
     */
    private setImage(): void {
        this.image = this.metadataTypes.includes(MentionMetadataTypesEnum.image);
    }
}
